import { useCallback } from 'react';
import { setWorkspaces, setCurrentWorkspace } from '../store/workspaceStore';
import {
  setCollections,
  setSchemas,
  setSelectedRequest,
  setSelectedCollection,
  setSelectedFolder,
} from '../store/collectionsStore';
import {
  setGlobalVariables,
  setWorkspaceVariables,
  setEnvironments,
  setSelectedEnvironment,
} from '../store/variablesStore';
import { generateLocalWorkspaceId, buildLocalManifestEntries } from '../utils/workspaceStorage';
import { dedupeCollectionRequestIds } from '../utils/localIds';
import { normalizeSchemas } from '../utils/schemaTree';
import { saveSnapshot, saveLocalWorkspaceManifest } from '../services/localWorkspaceStore';
import { cloneRepo as gitCloneRepo, parseGitHubRepo } from '../services/workspaceGitSync';
import { setWorkspaceGitLink } from '../services/accountsStore';
import { normalizeGitLink } from '../services/git/providers';
import { cloneTarget } from '../services/git/cloneTarget';
import { requireWorkspaceSlot, workspaceSlotRefusal } from '../plans/localWorkspaces';
import { adoptManifestProject } from '../scope/projectManifest';

/** Clone a workspace from a git repository. */
export function useWorkspaceClone({
  toastT,
  snapshotHydratedRef,
}) {
  /* ── Clone from GitHub ─────────────────────────────────────────────────── */
  const cloneWorkspaceFromGitHub = useCallback(async ({ repoInput, name, source }) => {
    // GitHub (no `source`), or any other host: a GitLab account or "Other git
    // server" with a Vault sign-in (cloneTarget.js).
    const parsed = source ? cloneTarget(repoInput, source) : parseGitHubRepo(repoInput);
    if (!parsed) {
      throw new Error('Enter a GitHub repo as "owner/repo" or a github.com URL.');
    }
    // Plans v2: a clone is a new local workspace.
    if (!requireWorkspaceSlot()) {
      throw Object.assign(new Error(workspaceSlotRefusal() || 'plan_limit'), { planLimit: true });
    }
    const newId = generateLocalWorkspaceId();
    const { branch, snapshot } = await gitCloneRepo(newId, {
      url: parsed.url,
      ...(source ? { provider: source.provider, accountId: source.accountId, vaultItemId: source.vaultItemId } : {}),
    });
    const git = normalizeGitLink(
      {
        owner: parsed.owner,
        repo: parsed.repo,
        branch: branch || 'main',
        url: parsed.url,
        ...(source ? { provider: source.provider } : {}),
        ...(source?.vaultItemId ? { vaultItemId: source.vaultItemId, vaultItemName: source.vaultItemName || '' } : {}),
      },
      source?.accountId ? { accountId: source.accountId } : undefined
    );
    const wsName = (name && name.trim()) || snapshot.workspaceName || parsed.repo;

    const newWorkspace = {
      id: newId,
      local_uid: newId,
      name: wsName,
      type: 'personal',
      storage: 'local',
      source: 'personal',
      role: 'owner',
      description: '',
      visibility: 'private',
      git,
      settings: { fork_copy_variables: true },
      createdAt: Date.now(),
    };

    await saveSnapshot(newId, {
      workspaceName: wsName,
      collections: snapshot.collections || [],
      globalVariables: snapshot.globalVariables || [],
      workspaceVariables: snapshot.workspaceVariables || [],
      environments: snapshot.environments || [],
      selectedEnvironmentId: snapshot.selectedEnvironmentId || '',
    });

    setWorkspaces((prev) => {
      const next = [...prev, newWorkspace];
      saveLocalWorkspaceManifest(buildLocalManifestEntries(next)).catch(() => {});
      return next;
    });
    setWorkspaceGitLink(newId, git).catch(() => {});
    // The repository's project (card 2.2): the clone lands in it.
    adoptManifestProject(newWorkspace, snapshot.project).catch(() => {});

    setCurrentWorkspace(newWorkspace);
    setCollections(dedupeCollectionRequestIds(snapshot.collections || []));
    setSchemas(normalizeSchemas(snapshot.schemas));
    setGlobalVariables(snapshot.globalVariables || []);
    setWorkspaceVariables(snapshot.workspaceVariables || []);
    const envs = snapshot.environments || [];
    setEnvironments(envs);
    setSelectedEnvironment(
      snapshot.selectedEnvironmentId
        ? envs.find((e) => String(e.id) === String(snapshot.selectedEnvironmentId)) || null
        : null
    );
    setSelectedRequest(null);
    setSelectedCollection(null);
    setSelectedFolder(null);
    snapshotHydratedRef.current = newId;
    toastT('toast.workspaceCloned', 'success', { name: wsName });
    return newWorkspace;
  }, [snapshotHydratedRef, toastT]);

  const handleWorkspaceCloned = useCallback(async ({ repoInput, name, source }) => {
    try {
      await cloneWorkspaceFromGitHub({ repoInput, name, source });
    } catch (error) {
      console.error('Clone workspace failed:', error);
      toastT('toast.workspaceCloneFailed', 'error', { message: error?.message || '' });
      throw error;
    }
  }, [cloneWorkspaceFromGitHub, toastT]);

  return { handleWorkspaceCloned };
}
